import * as React from 'react';
import {view, createViewConfig} from '@sail/engine';
import {Section, StripeLogo} from '@sail/helm';
import type {SectionProps} from '@sail/helm';
import {useIntl} from '@sail/intl';
import {Link} from '@sail/ui';
import * as messages from 'mkt-ssr/components/sail/SiteFooterSection/SiteFooter.messages';
import {globalMessages} from 'mkt-ssr/globalMessages';
import {SiteFooterList} from 'mkt-ssr/components/sail/SiteFooterSection/SiteFooterList';
import {styles} from 'mkt-ssr/components/sail/SiteFooterSection/SiteFooterSectionStyles';
import {styles as listStyles} from 'mkt-ssr/components/sail/SiteFooterSection/SiteFooterListStyles';
import {SiteFooterDisclaimerBrazil} from 'mkt-ssr/components/sail/SiteFooterSection/SiteFooterDisclaimerBrazil/SiteFooterDisclaimerBrazil';
import {SiteFooterListItem} from 'mkt-ssr/components/sail/SiteFooterSection/SiteFooterListItem';
import {SiteFooterLegalNotice} from 'mkt-ssr/components/sail/SiteFooterSection/SiteFooterLegalNotice';
import {LocaleGate} from 'mkt-ssr/components/LocaleGate/LocaleGate';
import {GlobalizationPicker} from 'mkt-ssr/components/sail/GlobalizationPicker/GlobalizationPicker';
import {useLinkResolver} from 'mkt-ssr/lib/routing/LinkResolver';
import {usePageMetadata} from 'mkt-ssr/lib/page/metadata';

export type SiteFooterSectionProps = SectionProps & {
  hasBorderTop?: boolean;
};

export const SiteFooterSectionConfig = createViewConfig({
  props: {} as SiteFooterSectionProps,
  name: 'SiteFooterSection',
});

/**
 * The global footer component used across stripe.com
 */
export const SiteFooterSection = SiteFooterSectionConfig.createView(
  ({hasBorderTop = false, ...props}) => {
    const {formatMessage} = useIntl();
    const {createRoute} = useLinkResolver();
    const {globalized} = usePageMetadata();

    return (
      <Section {...props} hasBorderTop={hasBorderTop}>
        <view.nav uses={[styles.footerNavContainerUnprocessedCss]}>
          <view.div
            uses={[
              styles.footerColumnContainer,
              styles.footerColumnContainerUnprocessedCss,
              styles.footerBrandColumn,
              styles.footerBrandColumnUnprocessedCss,
            ]}
          >
            <view.div uses={[styles.footerBrandAndGlobalization]}>
              <Link
                href={createRoute('https://stripe.com')}
                data-analytics-label="Stripe Logo"
                aria-label="Return to homepage"
                data-testid="footer-stripe-logo"
              >
                <StripeLogo uses={[styles.footerStripeLogo]} />
              </Link>
              {globalized !== false ? <GlobalizationPicker /> : null}
            </view.div>
            <view.p uses={[...styles.footerCopyrightNotice]}>
              © {new Date().getFullYear()} Stripe, Inc.
            </view.p>
          </view.div>
          <view.div
            uses={[
              styles.footerColumnContainer,
              styles.footerColumnContainerUnprocessedCss,
            ]}
          >
            <SiteFooterList
              title={formatMessage(messages.productsSectionTitle)}
            >
              <SiteFooterListItem
                label={formatMessage(messages.pricingLabel)}
                href={createRoute('https://stripe.com/pricing')}
              />
              <SiteFooterListItem
                label="Atlas"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/atlas')}
                testId="footer-products-atlas-list-item"
              />
              <SiteFooterListItem
                label="Billing"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/billing')}
                testId="footer-products-billing-list-item"
              />
              <SiteFooterListItem
                label="Capital"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/capital')}
                testId="footer-products-capital-list-item"
              />
              <SiteFooterListItem
                label="Checkout"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/payments/checkout')}
                testId="footer-products-checkout-list-item"
              />
              <SiteFooterListItem
                label="Climate"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/climate')}
                testId="footer-products-climate-list-item"
              />
              <SiteFooterListItem
                label="Connect"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/connect')}
                testId="footer-products-connect-list-item"
              />
              <SiteFooterListItem
                label="Data Pipeline"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/data-pipeline')}
                testId="footer-products-data-pipeline-list-item"
              />
              <SiteFooterListItem
                label="Elements"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/payments/elements')}
                testId="footer-products-elements-list-item"
              />
              <SiteFooterListItem
                label="Financial Connections"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/financial-connections')}
                testId="footer-products-elements-list-item"
              />
              <SiteFooterListItem
                label="Identity"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/identity')}
                testId="footer-products-identity-list-item"
              />
              <SiteFooterListItem
                label="Invoicing"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/invoicing')}
                testId="footer-products-invoicing-list-item"
              />
              <SiteFooterListItem
                label="Issuing"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/issuing')}
                testId="footer-products-issuing-list-item"
              />
              <SiteFooterListItem
                label="Link"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/payments/link')}
                testId="footer-products-link-list-item"
              />
              <SiteFooterListItem
                label="Payments"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/payments')}
                testId="footer-products-payments-list-item"
              />
              <SiteFooterListItem
                label="Payment Links"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/payments/payment-links')}
                testId="footer-products-payment-links-list-item"
              />
              <SiteFooterListItem
                label="Payouts"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/connect/payouts')}
                testId="footer-products-connect-payouts-list-item"
              />
              <SiteFooterListItem
                label="Radar"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/radar')}
                testId="footer-products-radar-list-item"
              />
              <SiteFooterListItem
                label="Revenue Recognition"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/revenue-recognition')}
                testId="footer-products-revenue-recognition-list-item"
              />
              <SiteFooterListItem
                label="Sigma"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/sigma')}
                testId="footer-products-sigma-list-item"
              />
              <SiteFooterListItem
                label="Tax"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/tax')}
                testId="footer-products-tax-list-item"
              />
              <SiteFooterListItem
                label="Terminal"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/terminal')}
                testId="footer-products-terminal-list-item"
              />
              <SiteFooterListItem
                label="Treasury"
                data-analytics-label="Product"
                href={createRoute('https://stripe.com/treasury')}
                testId="footer-products-treasury-list-item"
              />
            </SiteFooterList>
          </view.div>

          <view.div
            uses={[
              styles.footerColumnContainer,
              styles.footerColumnContainerUnprocessedCss,
            ]}
          >
            <SiteFooterList
              title={formatMessage(messages.solutionsSectionTitle)}
            >
              <SiteFooterListItem
                label={formatMessage(messages.startupsLabel)}
                href={createRoute('https://stripe.com/startups')}
                data-analytics-label="Startups"
                testId="footer-use-cases-startups-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.enterprisesLabel)}
                href={createRoute('https://stripe.com/enterprise')}
                data-analytics-label="Enterprises"
                testId="footer-use-cases-enterprise-list-item"
              />
              <SiteFooterListItem
                label="SaaS"
                href={createRoute('https://stripe.com/use-cases/saas')}
                data-analytics-label="SaaS"
                testId="footer-use-cases-saas-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.platformsLabel)}
                href={createRoute('https://stripe.com/use-cases/platforms')}
                data-analytics-label="Platforms"
                testId="footer-use-cases-platforms-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.ecommerceLabel)}
                href={createRoute('https://stripe.com/use-cases/ecommerce')}
                data-analytics-label="Ecommerce"
                testId="footer-use-cases-ecommerce-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.marketplacesLabel)}
                href={createRoute('https://stripe.com/use-cases/marketplaces')}
                data-analytics-label="Marketplaces"
                testId="footer-use-cases-marketplaces-list-item"
              />

              <LocaleGate exclude countries="TH">
                <SiteFooterListItem
                  label={formatMessage(messages.cryptoLabel)}
                  href={createRoute('https://stripe.com/use-cases/crypto')}
                  data-analytics-label="Crypto"
                  testId="footer-use-cases-crypto-list-item"
                />
              </LocaleGate>

              <SiteFooterListItem
                label={formatMessage(messages.creatorEconomyLabel)}
                href={createRoute(
                  'https://stripe.com/use-cases/creator-economy',
                )}
                data-analytics-label="Creator Economy"
                testId="footer-use-cases-creator-economy-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.embeddedFinanceLabel)}
                href={createRoute(
                  'https://stripe.com/use-cases/embedded-finance',
                )}
                data-analytics-label="Embedded"
                testId="footer-use-cases-embedded-finance-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.globalBusinessesLabel)}
                href={createRoute(
                  'https://stripe.com/use-cases/global-businesses',
                )}
                data-analytics-label="Global"
                testId="footer-company-global-businesses-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.inAppPaymentsLabel)}
                href={createRoute(
                  'https://stripe.com/use-cases/in-app-payments',
                )}
                data-analytics-label="In-app payments"
                testId="footer-company-in-app-payments-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.financeAutomationLabel)}
                href={createRoute(
                  'https://stripe.com/use-cases/finance-automation',
                )}
                data-analytics-label="Finance"
                testId="footer-use-cases-finance-automation-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.insuranceLabel)}
                href={createRoute('https://stripe.com/industries/insurance')}
                data-analytics-label="site_footer_insurance"
                testId="footer-use-cases-insurance-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.mediaAndEntertainmentLabel)}
                href={createRoute(
                  'https://stripe.com/industries/media-entertainment',
                )}
                data-analytics-label="site_footer_media_and_entertainment"
                testId="footer-use-cases-media-and-entertainment-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.nonprofitsLabel)}
                href={createRoute('https://stripe.com/industries/nonprofits')}
                data-analytics-label="site_footer_nonprofits"
                testId="footer-use-cases-nonprofits-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.retailLabel)}
                href={createRoute('https://stripe.com/industries/retail')}
                data-analytics-label="site_footer_retail"
                testId="footer-use-cases-retail-list-item"
              />
            </SiteFooterList>
            <SiteFooterList
              title={formatMessage(messages.integrationsSectionTitle)}
            >
              <SiteFooterListItem
                label="Stripe App Marketplace"
                href={createRoute('https://marketplace.stripe.com')}
                data-analytics-label="App MarketPlace"
                testId="footer-solutions-app-marketplace-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.partnerEcosystemLabel)}
                href={createRoute('https://stripe.com/partners')}
                data-analytics-label="Partner Ecosystem"
                testId="footer-solutions-partners-ecosystem-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.professionalServicesLabel)}
                href={createRoute('https://stripe.com/professional-services')}
                data-analytics-label="Professional Services"
                testId="footer-solutions-professional-services-list-item"
              />
            </SiteFooterList>
            <SiteFooterList
              title={formatMessage(messages.developersSectionTitle)}
            >
              <SiteFooterListItem
                label={formatMessage(messages.documentationLabel)}
                href={createRoute('https://docs.stripe.com')}
                data-analytics-label="Docs"
                testId="footer-developers-docs-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.apiReferenceLabel)}
                href={createRoute('https://docs.stripe.com/api')}
                data-analytics-label="Docs API"
                testId="footer-developers-api-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.apiStatusLabel)}
                href={createRoute('https://status.stripe.com/')}
                data-analytics-label="System status"
                testId="footer-developers-system-status-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.apiChangelogLabel)}
                href={createRoute('https://docs.stripe.com/changelog')}
                data-analytics-label="Docs API Changelog"
                testId="footer-developers-api-changelog-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.librariesAndSdksLabel)}
                href={createRoute('https://docs.stripe.com/development')}
                data-analytics-label="Libraries and SDKs"
                testId="footer-developers-libraries_and_sdks-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.developerBlogLabel)}
                href={createRoute('https://stripe.dev/')}
                data-analytics-label="Developer blog"
                testId="footer-developers-developer-blog-list-item"
              />
            </SiteFooterList>
          </view.div>

          <view.div
            uses={[
              styles.footerColumnContainer,
              styles.footerColumnContainerUnprocessedCss,
            ]}
          >
            <SiteFooterList
              title={formatMessage(messages.resourcesSectionTitle)}
            >
              <SiteFooterListItem
                label={formatMessage(messages.guidesLabel)}
                href={createRoute('https://stripe.com/guides')}
                data-analytics-label="Education"
                testId="footer-resources-guides-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.customerStoriesLabel)}
                href={createRoute('https://stripe.com/customers')}
                data-analytics-label="Customer Stories"
                testId="footer-resources-customer-stories-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.blogLabel)}
                href={createRoute('https://stripe.com/blog')}
                data-analytics-label="Blog"
                testId="footer-resources-blog-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.annualConferenceLabel)}
                href={createRoute('https://stripe.com/sessions')}
                data-analytics-label="Sessions Annual Conference"
                testId="footer-resources-sessions-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.privacyTermsLabel)}
                href={createRoute('https://stripe.com/privacy')}
                data-analytics-label="Legal"
                testId="footer-resources-privacy-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.prohibitedBusinessesLabel)}
                href={createRoute(
                  'https://stripe.com/legal/restricted-businesses',
                )}
                data-analytics-label="Restricted businesses"
                testId="footer-resources-restricted-businesses-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.licensesLabel)}
                href={createRoute('https://stripe.com/legal/spc/licenses')}
                data-analytics-label="Licenses"
                testId="footer-resources-licenses-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.sitemapLabel)}
                href={createRoute('https://stripe.com/sitemap')}
                data-analytics-label="Sitemap"
                testId="footer-resources-sitemap-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.cookieSettingsLabel)}
                href={createRoute('https://stripe.com/cookie-settings')}
                data-analytics-label="Cookie settings"
                testId="footer-resources-cookie-settings-list-item"
              />

              <SiteFooterLegalNotice />
              <SiteFooterListItem
                label={formatMessage(messages.moreResourcesLabel)}
                href={createRoute('https://stripe.com/resources/more')}
                data-analytics-label="More Resources"
                testId="footer-resources-more-resources-list-item"
              />
            </SiteFooterList>
            <SiteFooterList title={formatMessage(messages.companySectionTitle)}>
              <LocaleGate countries="BR">
                <SiteFooterListItem
                  label={formatMessage(messages.institutionalLinkLabel)}
                  href={createRoute('https://stripe.com/institutional')}
                  data-analytics-label="Company"
                />
              </LocaleGate>
              <SiteFooterListItem
                label={formatMessage(messages.jobsLabel)}
                href={createRoute('https://stripe.com/jobs')}
                data-analytics-label="Careers"
                testId="footer-company-jobs-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.newsroomLabel)}
                href={createRoute('https://stripe.com/newsroom')}
                data-analytics-label="Newsroom"
                testId="footer-company-newsroom-list-item"
              />
              <SiteFooterListItem
                label="Stripe Press"
                href={createRoute('https://press.stripe.com/')}
                data-analytics-label="Stripe Press"
                testId="footer-company-stripe-press-list-item"
              />
            </SiteFooterList>
            <SiteFooterList title={formatMessage(messages.supportSectionTitle)}>
              <SiteFooterListItem
                label={formatMessage(globalMessages.contactSales)}
                href={createRoute('https://stripe.com/contact/sales')}
                data-analytics-label="Contact Sales"
                testId="footer-resources-contact-sales-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.supportCenterLabel)}
                href={createRoute(
                  'https://support.stripe.com/?referrerLocale=en-us',
                )}
                data-analytics-label="Support request"
                testId="footer-resources-support-list-item"
              />
              <SiteFooterListItem
                label={formatMessage(messages.supportPlansLabel)}
                href={createRoute('https://stripe.com/support-plans')}
                data-analytics-label="Support Plans"
                testId="footer-resources-support-plans-list-item"
              />
              <LocaleGate countries="US">
                <SiteFooterListItem
                  label={
                    <>
                      <view.span uses={[listStyles.supportLinkNoWrap]}>
                        {formatMessage(messages.caPhoneLabel)}
                      </view.span>{' '}
                      <view.span
                        uses={[listStyles.supportLinkNoWrap]}
                        data-nosnippet
                      >
                        +1 888 926 2289
                      </view.span>
                    </>
                  }
                />
              </LocaleGate>
            </SiteFooterList>
          </view.div>

          <LocaleGate countries="BR">
            <SiteFooterDisclaimerBrazil />
          </LocaleGate>
        </view.nav>
      </Section>
    );
  },
);
